'use client';

import { useMemo, useState } from 'react';

import classnames from 'classnames';
import { IoImageOutline, IoTrashOutline } from 'react-icons/io5';
import ImageUploading, {
  type ImageListType,
  type ImageType,
} from 'react-images-uploading';

import { resizeImageFile } from '../../utils/image-resizer';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import type { ImageUploadProps } from './ImageUpload.types';

import styles from './ImageUpload.module.scss';

function ImageUpload({
  style,
  className,
  dataURL,
  resize = true,
  width,
  height,
  maxWidth,
  maxHeight,
  compressFormat = 'WEBP',
  onChange,
  onError,
  testId = 'ui-image-upload',
}: ImageUploadProps) {
  const [loading, setLoading] = useState(false);
  const [userModifiedImages, setUserModifiedImages] =
    useState<ImageListType | null>(null);

  // Derive images from props using useMemo instead of syncing in useEffect
  const imagesFromProps = useMemo(() => {
    if (dataURL !== undefined && dataURL !== '') {
      return [
        {
          dataURL,
        },
      ];
    }
    return [];
  }, [dataURL]);

  // Use user-modified images if they exist, otherwise use derived images from props
  const images = userModifiedImages ?? imagesFromProps;

  // Derive single image from images array
  const image: ImageType | undefined = useMemo(() => images[0], [images]);

  const resizeFile = (file: File) =>
    resizeImageFile(file, {
      maxWidth,
      maxHeight,
      compressFormat,
      quality: 100,
    });

  const onChangeImage = async (imageList: ImageListType) => {
    setLoading(true);
    setUserModifiedImages(imageList);

    try {
      if (imageList[0] !== undefined && imageList[0].file !== undefined) {
        let uri = undefined;

        if (resize) {
          uri = await resizeFile(imageList[0].file);
        } else {
          uri = imageList[0].dataURL ?? '';
        }

        onChange(uri);
      } else {
        onChange(undefined);
      }
    } catch {
      setUserModifiedImages(null);
      onError({ resize: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={style}
      className={classnames(styles['image-upload'], className)}
      data-testid={testId}
    >
      <ImageUploading
        multiple={false}
        value={images}
        onChange={onChangeImage}
        onError={(errors) => onError(errors ?? undefined)}
        maxNumber={1}
        acceptType={['jpg', 'jpeg', 'png']}
        maxFileSize={5242880}
      >
        {({ onImageUpload, onImageUpdate, onImageRemove, isDragging }) => (
          <div className={styles['container']}>
            {image !== undefined && (
              <img
                className={styles['image']}
                alt={(image as ImageType)?.file?.name ?? 'thumbnail'}
                src={image.dataURL}
                height={height}
                width={width}
              />
            )}
            {loading && (
              <Icon
                icon={<IoImageOutline />}
                className={classnames(loading && styles['icon--loading'])}
                role="img"
                aria-label="upload-button"
              />
            )}
            {!loading && images.length === 0 && (
              <IconButton
                icon={<IoImageOutline />}
                className={classnames(isDragging && styles['icon--dragging'])}
                onClick={onImageUpload}
                aria-label="upload-button"
              />
            )}
            {!loading && images.length === 1 && (
              <div className={styles['options']}>
                <IconButton
                  icon={<IoImageOutline />}
                  onClick={() => onImageUpdate(0)}
                  aria-label="upload-button"
                />
                <IconButton
                  icon={<IoTrashOutline />}
                  onClick={() => onImageRemove(0)}
                  aria-label="remove-button"
                />
              </div>
            )}
          </div>
        )}
      </ImageUploading>
    </div>
  );
}

export { ImageUpload };
